import type { RefObject } from 'react';
import { useEffect, useState } from 'react';
import { useScroll } from 'react-use';

import { useResizeObserver } from '~/modules/artist-roster/components/ArtistContactInfo/hooks/useResizeObserver';

export const TOP_THRESHOLD_PIXELS = 40;
export const BOTTOM_THRESHOLD_PIXELS = 40;

interface GetIsScrollMessageVisibleParams {
  ref: RefObject<HTMLDivElement>;
  scrollDistance: number;
}

/**
 * Determines if the scroll message should be visible based on the scroll position.
 * @param param0 - The parameters containing the ref and scroll distance.
 * @returns True if the scroll message should be visible, false otherwise.
 *
 * scrollHeight: is the height including content not visible due to overflow.
 * clientHeight: is the inner height of an element.
 */
const getIsScrollMessageVisible = ({
  ref,
  scrollDistance,
}: GetIsScrollMessageVisibleParams) => {
  // If container is not rendered yet, hide the message.
  if (!ref.current) {
    return false;
  }

  // If container is barely scrollable (content almost fits), hide the message.
  if (
    ref.current.scrollHeight <=
    ref.current.clientHeight + BOTTOM_THRESHOLD_PIXELS
  ) {
    return false;
  }

  // If container is not scrolled yet, show the message.
  if (!scrollDistance) {
    return true;
  }

  // If container isn't scrolled past the BOTTOM_THRESHOLD_PIXELS, show the message.
  if (
    ref.current.scrollHeight - scrollDistance >
    ref.current.clientHeight + BOTTOM_THRESHOLD_PIXELS
  ) {
    return true;
  }

  return false;
};

/**
 * React Hook to manage the visibility of the scroll message.
 * @param ref - Reference to the scrollable element.
 * @returns An object containing the scroll state.
 */
export function useScrollMessage(ref: RefObject<HTMLDivElement>) {
  const { y: scrollDistance } = useScroll(ref);

  // Fade the "Scroll for more" message after scrolling down by 40px.
  const isScrolledToTop = ref?.current
    ? ref.current.scrollTop < TOP_THRESHOLD_PIXELS
    : true;
  // Hide the "Scroll for more" message as it reaches the bottom 40px.
  const [isScrollMessageVisible, setIsScrollMessageVisible] = useState(false);

  useEffect(() => {
    setIsScrollMessageVisible(
      getIsScrollMessageVisible({ ref: ref, scrollDistance }),
    );
  }, [ref, scrollDistance]);

  const handleResize = () => {
    setIsScrollMessageVisible(
      getIsScrollMessageVisible({ ref: ref, scrollDistance }),
    );
  };

  /**
   * The ContactCards component and ScrollMessage component are siblings. But since the ScrollMessage
   * has no way of knowing when the ContactsCards component has finished rendering, we observe the parent
   * element for size changes, and in return invoke our logic via the callback function.
   */
  useResizeObserver(ref, handleResize);

  return { isScrolledToTop, isScrollMessageVisible };
}
